import { z } from 'zod';
import {
  ADMIN_API_PATH,
  CharactersResponseSchema,
  RsvpsResponseSchema,
  RsvpSubmitResponseSchema,
} from './types';
import type { AdminPatchInput, CharacterRow, RsvpInput, RsvpRow, RsvpSubmitResponse } from './types';

const ApiResultSchema = z.union([
  RsvpSubmitResponseSchema,
  z.object({ error: z.string(), issues: z.unknown().optional() }),
]);

export async function fetchCharacters(): Promise<CharacterRow[]> {
  const r = await fetch('/api/characters');
  if (!r.ok) throw new Error('failed to load characters');
  const data = CharactersResponseSchema.parse(await r.json());
  return data.characters;
}

export type SubmitResult =
  | { ok: true; rsvp: RsvpSubmitResponse['rsvp'] }
  | { ok: false; error: string; issues?: unknown };

export async function submitRsvp(input: RsvpInput): Promise<SubmitResult> {
  const r = await fetch('/api/rsvp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = ApiResultSchema.parse(await r.json());
  if (r.ok && 'ok' in data) return data;
  if ('error' in data) return { ok: false, error: data.error, issues: data.issues };
  return { ok: false, error: 'unknown_response' };
}

export async function fetchAdminRsvps(): Promise<RsvpRow[]> {
  const r = await fetch(`${ADMIN_API_PATH}/rsvps`);
  if (!r.ok) throw new Error('admin fetch failed');
  const data = RsvpsResponseSchema.parse(await r.json());
  return data.rsvps;
}

export async function patchAdminRsvp(id: number, body: Partial<AdminPatchInput>): Promise<void> {
  const r = await fetch(`${ADMIN_API_PATH}/rsvps/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`patch failed: ${r.status}`);
}

export async function releaseCharacter(rsvp_id: number): Promise<void> {
  const r = await fetch(`${ADMIN_API_PATH}/release-character`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rsvp_id }),
  });
  if (!r.ok) throw new Error('release failed');
}

export async function deleteRsvp(rsvp_id: number): Promise<void> {
  const r = await fetch(`${ADMIN_API_PATH}/delete-rsvp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rsvp_id }),
  });
  if (!r.ok) throw new Error('delete failed');
}
